import { myVisitCounselApi, type PlannerApiError } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { MovingItemRequest } from '@/entities/visit-counsel/model/room-observing.types';
import { requestUploadToken, uploadToPresignedUrl } from '@/entities/unstructured';
import type { EditTarget } from '@/features/visit-counsel/room-observing-flow/ui/EditTargetSelector';
import { pollCounselUntilObservingMovingConfirmed } from './pollCounselUntilObservingMovingConfirmed';
import {
  buildObservingVideoUploadJobId,
  deletePendingObservingVideoUpload,
  savePendingObservingVideoUpload,
  type PendingObservingVideoUploadRecord,
} from './pendingObservingVideoUploadStore';

const inFlightJobIds = new Set<string>();

export interface ObservingVideoUploadFlowParams {
  visitCounselId: string;
  sessionId: string;
  blob: Blob;
  mimeType: string;
  isAdditionalSpaceMoving: boolean;
  isFirstStart: boolean;
  saveItems: boolean;
  editTarget?: EditTarget;
  isVideoOnlyForAdditionalSpace: boolean;
  items: MovingItemRequest[];
  /** handleComplete (1) 짐/세션 start 가 이미 끝났으면 true */
  itemsPersistedBeforeUpload: boolean;
}

export interface ObservingVideoUploadFlowDeps {
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  markPending: (sessionId: string) => void;
  clearPending: (sessionId: string) => void;
  onAfterDone?: (counsel: VisitCounselView) => void;
  onCancel?: () => void;
  resumedToastMessage?: string;
}

function paramsFromRecord(
  record: PendingObservingVideoUploadRecord
): Omit<ObservingVideoUploadFlowParams, 'blob' | 'mimeType'> {
  return {
    visitCounselId: record.visitCounselId,
    sessionId: record.sessionId,
    isAdditionalSpaceMoving: record.isAdditionalSpaceMoving,
    isFirstStart: record.isFirstStart,
    saveItems: record.saveItems,
    editTarget: record.editTarget,
    isVideoOnlyForAdditionalSpace: record.isVideoOnlyForAdditionalSpace,
    items: record.items,
    itemsPersistedBeforeUpload: record.itemsPersistedBeforeUpload,
  };
}

async function persistItemsIfNeeded(record: PendingObservingVideoUploadRecord): Promise<void> {
  if (record.itemsPersistedBeforeUpload) return;

  const {
    visitCounselId,
    sessionId,
    isAdditionalSpaceMoving,
    isFirstStart,
    saveItems,
    isVideoOnlyForAdditionalSpace,
    items,
  } = record;

  if (saveItems) {
    if (isAdditionalSpaceMoving) {
      if (isFirstStart) {
        await myVisitCounselApi.startAdditionalSpaceObservingSession(visitCounselId, sessionId, {
          movingItems: items,
        });
      } else {
        await myVisitCounselApi.recordAdditionalSpaceObservingMovingItems(
          visitCounselId,
          sessionId,
          { movingItems: items }
        );
      }
    } else if (isFirstStart) {
      await myVisitCounselApi.startRoomObservingSession(visitCounselId, sessionId, {
        movingItems: items,
      });
    } else {
      await myVisitCounselApi.recordRoomObservingMovingItems(visitCounselId, sessionId, {
        movingItems: items,
      });
    }
  } else if (isVideoOnlyForAdditionalSpace && isFirstStart) {
    await myVisitCounselApi.startAdditionalSpaceObservingSession(visitCounselId, sessionId, {
      movingItems: [],
    });
  }

  await savePendingObservingVideoUpload({
    ...record,
    itemsPersistedBeforeUpload: true,
  });
}

async function putToPresignedWithTokenRefresh(
  record: PendingObservingVideoUploadRecord
): Promise<PendingObservingVideoUploadRecord> {
  const blob = record.blob;
  if (!blob) {
    throw new Error('업로드할 영상이 없습니다. 다시 촬영해 주세요.');
  }

  const file = new File([blob], 'room-video.webm', { type: record.contentType });
  const tryUpload = async (url: string) => uploadToPresignedUrl(url, file, record.contentType);

  try {
    await tryUpload(record.uploadUrl);
    return record;
  } catch (firstErr) {
    const upload = await requestUploadToken({
      resourceType: 'VIDEO',
      subType: 'VISIT_COUNSEL_VIDEO_CLIP',
      contentType: record.contentType,
    });
    const next: PendingObservingVideoUploadRecord = {
      ...record,
      structuredToken: upload.structuredToken,
      uploadUrl: upload.uploadUrl,
    };
    await savePendingObservingVideoUpload(next);
    try {
      await tryUpload(upload.uploadUrl);
      return next;
    } catch {
      throw firstErr instanceof Error ? firstErr : new Error('영상 업로드에 실패했습니다.');
    }
  }
}

async function applyVideoAndCompleteSession(
  record: PendingObservingVideoUploadRecord
): Promise<void> {
  const { visitCounselId, sessionId, structuredToken, isAdditionalSpaceMoving } = record;

  if (isAdditionalSpaceMoving) {
    await myVisitCounselApi.recordAdditionalSpaceObservingVideo(visitCounselId, sessionId, {
      videoToken: structuredToken,
    });
    await myVisitCounselApi.completeAdditionalSpaceObservingSession(visitCounselId, sessionId);
  } else {
    await myVisitCounselApi.recordRoomObservingVideo(visitCounselId, sessionId, {
      videoToken: structuredToken,
    });
    await myVisitCounselApi.completeRoomObservingSession(visitCounselId, sessionId);
  }
}

function showObservingVideoSuccessToast(
  params: Omit<ObservingVideoUploadFlowParams, 'blob' | 'mimeType'>,
  deps: ObservingVideoUploadFlowDeps
): void {
  const { editTarget, isVideoOnlyForAdditionalSpace, isFirstStart } = params;

  if (editTarget === 'video' || isVideoOnlyForAdditionalSpace) {
    deps.showToast(isFirstStart ? '영상이 저장되었습니다.' : '영상이 수정되었습니다.', 'success');
    return;
  }
  if (isFirstStart) {
    deps.showToast('짐 기록과 영상이 저장되었습니다.', 'success');
    return;
  }
  deps.showToast('짐 기록과 영상이 수정되었습니다.', 'success');
}

function startBackgroundObservingPoll(
  record: PendingObservingVideoUploadRecord,
  deps: ObservingVideoUploadFlowDeps
): void {
  deps.markPending(record.sessionId);
  void pollCounselUntilObservingMovingConfirmed(
    deps.refetch,
    record.sessionId,
    record.isAdditionalSpaceMoving ? 'additional' : 'room',
    { expectComplete: true }
  )
    .catch(() => {})
    .finally(() => {
      deps.clearPending(record.sessionId);
    });
}

/**
 * 세션 상태 충돌(409)로 서버가 영구히 거부하는 에러인지 판별한다.
 * 대표: VISIT_COUNSEL_009 (Completed 이후 영상 기록/수정 불가).
 * 세션은 COMPLETED 에서 STARTED 로 돌아가지 않으므로 재시도해도 영원히 실패한다.
 */
function isSessionConflictError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const { code, httpStatus } = err as PlannerApiError;
  return code === 'VISIT_COUNSEL_009' || httpStatus === 409;
}

async function finishAfterPresignedUpload(
  record: PendingObservingVideoUploadRecord,
  deps: ObservingVideoUploadFlowDeps
): Promise<void> {
  try {
    await persistItemsIfNeeded(record);
    await applyVideoAndCompleteSession(record);
  } catch (err) {
    if (isSessionConflictError(err)) {
      // 세션이 이미 COMPLETED — 재시도해도 영원히 409 이므로 작업을 종결한다.
      // (이 처리가 없으면 페이지 mount 마다 완료된 세션에 영상 PUT 을 반복한다)
      await deletePendingObservingVideoUpload(record.id);
      deps.showToast(
        '이미 완료된 세션이라 대기 중이던 영상 업로드를 종료했습니다. 영상 누락 시 운영팀에 알려주세요.',
        'warning'
      );
      await deps.refetch().catch(() => undefined);
      return;
    }
    throw err;
  }

  // 서버 반영이 확정된 즉시 큐에서 제거한다.
  // 이후 refetch/폴링이 실패해도 완료된 작업이 재개 대상으로 남지 않는다.
  await deletePendingObservingVideoUpload(record.id);

  const params = paramsFromRecord(record);
  if (deps.resumedToastMessage) {
    deps.showToast(deps.resumedToastMessage, 'success');
  } else {
    showObservingVideoSuccessToast(params, deps);
  }

  const initial = await deps.refetch();
  const initialCounsel = initial?.counsel ?? null;
  if (initialCounsel && deps.onAfterDone) deps.onAfterDone(initialCounsel);
  else if (deps.onCancel) deps.onCancel();

  startBackgroundObservingPoll(record, deps);
}

/**
 * presigned 업로드 구간을 IndexedDB 에 남긴 뒤 S3 PUT → 옵저빙 API 를 실행한다.
 */
export async function runObservingVideoUploadWithPersistence(
  params: ObservingVideoUploadFlowParams,
  deps: ObservingVideoUploadFlowDeps
): Promise<void> {
  const jobId = buildObservingVideoUploadJobId(params.visitCounselId, params.sessionId);
  if (inFlightJobIds.has(jobId)) return;

  const contentType = params.mimeType || 'video/webm';
  const upload = await requestUploadToken({
    resourceType: 'VIDEO',
    subType: 'VISIT_COUNSEL_VIDEO_CLIP',
    contentType,
  });

  let record: PendingObservingVideoUploadRecord = {
    id: jobId,
    visitCounselId: params.visitCounselId,
    sessionId: params.sessionId,
    phase: 'awaiting_presigned_upload',
    structuredToken: upload.structuredToken,
    uploadUrl: upload.uploadUrl,
    contentType,
    blob: params.blob,
    createdAt: Date.now(),
    itemsPersistedBeforeUpload: params.itemsPersistedBeforeUpload,
    isAdditionalSpaceMoving: params.isAdditionalSpaceMoving,
    isFirstStart: params.isFirstStart,
    saveItems: params.saveItems,
    editTarget: params.editTarget,
    isVideoOnlyForAdditionalSpace: params.isVideoOnlyForAdditionalSpace,
    items: params.items,
  };

  await savePendingObservingVideoUpload(record);
  inFlightJobIds.add(jobId);
  deps.markPending(params.sessionId);

  try {
    record = await putToPresignedWithTokenRefresh(record);
    record = {
      ...record,
      phase: 'presigned_uploaded',
      blob: undefined,
    };
    await savePendingObservingVideoUpload(record);

    await finishAfterPresignedUpload(record, deps);
  } finally {
    inFlightJobIds.delete(jobId);
    deps.clearPending(params.sessionId);
  }
}

/**
 * 새로고침 등으로 남은 pending job 을 재개한다.
 */
export async function resumePendingObservingVideoUpload(
  record: PendingObservingVideoUploadRecord,
  deps: ObservingVideoUploadFlowDeps
): Promise<void> {
  if (inFlightJobIds.has(record.id)) return;
  inFlightJobIds.add(record.id);
  deps.markPending(record.sessionId);

  try {
    let current = record;
    if (current.phase === 'awaiting_presigned_upload') {
      current = await putToPresignedWithTokenRefresh(current);
      current = {
        ...current,
        phase: 'presigned_uploaded',
        blob: undefined,
      };
      await savePendingObservingVideoUpload(current);
    }

    await finishAfterPresignedUpload(current, {
      ...deps,
      resumedToastMessage: deps.resumedToastMessage ?? '영상 업로드가 이어서 완료되었습니다.',
      onAfterDone: undefined,
      onCancel: undefined,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : '영상 업로드 재개에 실패했습니다';
    deps.showToast(msg, 'error');
  } finally {
    inFlightJobIds.delete(record.id);
    deps.clearPending(record.sessionId);
  }
}
